import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Text } from '../../ui'
import { socialService } from '../../../services/social'
import { SocialEdit } from '../../../types/social'
import MentionText from './MentionText'
import { formatSocialDate } from './socialUtils'

export interface EditHistoryModalProps {
  title: string
  postId?: string
  commentId?: string
  currentContent: string
  onClose: () => void
}

const EditHistoryModal: React.FC<EditHistoryModalProps> = ({
  title,
  postId,
  commentId,
  currentContent,
  onClose,
}) => {
  const [edits, setEdits] = useState<SocialEdit[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await socialService.listEditHistory({ postId, commentId })
        if (!cancelled) setEdits(data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar histórico')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [postId, commentId])

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[80vh] overflow-hidden rounded-xl bg-white dark:bg-gray-800 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          <div>
            <Text size="xs" color="muted" className="font-medium uppercase tracking-wide mb-1">
              Versão atual
            </Text>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              <MentionText content={currentContent} />
            </p>
          </div>

          {loading && (
            <Text size="sm" color="muted">Carregando histórico...</Text>
          )}

          {error && (
            <Text size="sm" className="text-red-600 dark:text-red-400">{error}</Text>
          )}

          {!loading && !error && edits.length === 0 && (
            <Text size="sm" color="muted">Nenhuma versão anterior registrada.</Text>
          )}

          {edits.map((edit) => (
            <div
              key={edit.id}
              className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40"
            >
              <Text size="xs" color="muted" className="mb-1">
                {formatSocialDate(edit.edited_at)}
                {edit.editor?.name ? ` · ${edit.editor.name}` : ''}
              </Text>
              <p className="text-sm text-gray-800 dark:text-gray-200">
                <MentionText content={edit.previous_content} />
              </p>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default EditHistoryModal
